import React, { useState, useEffect } from 'react';
import { useFileContext } from '../../ContextAPI/FileContext';
import './ToggleSwitch.css';
import { ManagerlApi } from '../../API/api';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { FaTrash } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

const SlopesCalcTab = () => {
  const { isSlopesCalcEnabled, setisSlopesCalcEnabled, setRunSlopesCalc } = useFileContext();

  const [folderPath, setFolderPath] = useState('');
  const [showCheckMark, setShowCheckMark] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState('');
  const [requests, setRequests] = useState([]);
  const [dbName, setDbName] = useState('');
  const [collectionName, setCollectionName] = useState('');
  const [maxServers, setMaxServers] = useState(1);

  // Algorithm parameters
  const [sectionHeight, setSectionHeight] = useState(5);
  const [minDistanceBetweenPoints, setMinDistanceBetweenPoints] = useState(1.5);
  const [sectionBinsSize, setSectionBinsSize] = useState(0.2);
  const [minBinPointsPercent, setMinBinPointsPercent] = useState(0.05);
  const [addSlopePointsToSectionImage, setAddSlopePointsToSectionImage] = useState(true);
  const [aerialData, setAerialData] = useState(false);
  const [dlModel, setDlModel] = useState(false);

  const [units, setUnits] = useState('feet');
  const [maxXToCalc, setMaxXToCalc] = useState(40);
  const [twoWayAnalysis, setTwoWayAnalysis] = useState(false);

  // Guardrail Warrent Rules: Kentucky, Arkansas, or Manually Defined
  const kentuckyRules = [
    { min: 5, max: Infinity, slope: -0.5 },
    { min: 5, max: 6, slope: -0.4667 },
    { min: 6, max: 7, slope: -0.4333 },
    { min: 7, max: 8, slope: -0.4 },
    { min: 8, max: 9, slope: -0.3667 },
    { min: 9, max: 10, slope: -0.333 },
    { min: 10, max: Infinity, slope: -0.333 }
  ];

  const arkansasRules = [
    { min: 5, max: Infinity, slope: -0.5 },
  ];

  const [grRules, setGrRules] = useState(kentuckyRules); // Default to Kentucky rules
  const [selectedRuleSet, setSelectedRuleSet] = useState('Kentucky'); // Default rule set

  useEffect(() => {
    if (selectedRuleSet === 'Kentucky') {
      setGrRules(kentuckyRules);
    } else if (selectedRuleSet === 'Arkansas') {
      setGrRules(arkansasRules);
    } else {
      setGrRules([{ min: 0, max: 0, slope: 0 }]); // Empty rules for manual input
    }
  }, [selectedRuleSet]);

  useEffect(() => {
    if(aerialData)
      setSectionBinsSize(1.5)
    else
      setSectionBinsSize(0.2)
  }, [aerialData]);

  useEffect(() => {
    if(units === "meter") {
      setMaxXToCalc(Math.round(parseFloat(maxXToCalc) / 3.28084 * 1000) / 1000);
      setSelectedRuleSet("Manual");
    }
    else {
      setMaxXToCalc(40);
      setSelectedRuleSet("Kentucky");
    }
  }, [units]);

  const formatGrRulesForRequest = (rules) => {
    return rules.map(rule => [
      rule.min,
      rule.max === Infinity ? 'inf' : rule.max, // Handle Infinity in the frontend
      rule.slope
    ]);
  };

  const handleGrRuleChange = (index, field, value) => {
    const updatedRules = [...grRules];
  
    if (field === 'max') {
      // Handle Infinity values
      if (value.toLowerCase() === 'infinity' || value.toLowerCase() === 'inf') {
        updatedRules[index][field] = Infinity;
      } else {
        updatedRules[index][field] = parseFloat(value) || Infinity; // Default to "Infinity" if invalid
      }
    } else if (field === 'slope') {
      // Allow typing negative sign, empty, or valid float values
      if (value === '-' || value === '') {
        updatedRules[index][field] = value; // Temporarily store "-" or empty string
      } else {
        updatedRules[index][field] = parseFloat(value) || 0; // Parse float or default to 0
      }
    } else {
      updatedRules[index][field] = parseFloat(value); // Default to 0 if invalid
    }
  
    setGrRules(updatedRules);
  };

  const addGrRule = () => {
    setGrRules([...grRules, { min: 0, max: "Infinity", slope: 0 }]);
  };

  const removeGrRule = (index) => {
    const updatedRules = grRules.filter((_, i) => i !== index);
    setGrRules(updatedRules);
  };

  const handleToggle = () => {
    setisSlopesCalcEnabled(!isSlopesCalcEnabled);
  };

  const extractFolderId = (url) => {
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

  const handleRunSlopesCalc = async () => {
    const folderId = extractFolderId(folderPath);
    if (!folderId) {
      console.error('Invalid folder URL');
      return;
    }

    const apiUrl = `${ManagerlApi}/slope-calc/${folderId}`;

    setCurrentFolderId(folderId);
    setShowCheckMark(false);

    const requestId = uuidv4();
    
    // Create slopes_params object
    const slopes_params = {
      section_height: Number(sectionHeight),
      min_distance_between_points: Number(minDistanceBetweenPoints),
      section_bins_size: Number(sectionBinsSize),
      min_bin_points_precent: Number(minBinPointsPercent),
      max_x_to_calc: Number(maxXToCalc),
      units: units,
      add_slope_points_to_section_image: addSlopePointsToSectionImage,
      DL_Model: dlModel,
      aerial_data: aerialData,
      two_way_analysis: twoWayAnalysis,
      slopes_warrent_categories: formatGrRulesForRequest(grRules)
    };

    // Get current date and time
    const startTime = new Date().toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    setRequests((prevRequests) => [
      ...prevRequests,
      {
        id: requestId,
        folderId,
        dbName,
        collectionName,
        maxServers,
        status: 'In Progress',
        slopes_params: slopes_params,  // Save slopes params
        startTime,  // Record the start time
        loading: true,
        showDetails: false,
      },
    ]);

    setFolderPath('');
    setDbName('');
    setCollectionName('');

    try {
      const requestBody = {
        db: dbName,
        collection: collectionName,
        max_ec2_instances: Number(maxServers),
        slopes_params: slopes_params
      };
      console.log(requestBody)
      const response = await axios.post(apiUrl, requestBody, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const statusMessage = response.data.message || 'Task Completed';
      setRequests((prevRequests) =>
        prevRequests.map((request) =>
          request.id === requestId
            ? { ...request, status: `${statusMessage} ✅`, loading: false }
            : request
        )
      );
    } catch (error) {
      console.error('There was an error running slopes calc!', error);

      const errorMessage = error.response?.data?.message || 'Task Failed';
      setRequests((prevRequests) =>
        prevRequests.map((request) =>
          request.id === requestId
            ? { ...request, status: `${errorMessage} ❌`, loading: false }
            : request
        )
      );
    }
  };

  const handleDeleteRequest = (id) => {
    setRequests((prevRequests) =>
      prevRequests.filter((request) => request.id !== id)
    );
  };

  const handleClearAll = () => {
    setRequests([]);
  };

  const handleToggleDetails = (id) => {
    setRequests((prevRequests) =>
      prevRequests.map((request) =>
        request.id === id ? { ...request, showDetails: !request.showDetails } : request
      )
    );
  };

  useEffect(() => {
    setRunSlopesCalc(() => handleRunSlopesCalc);
  }, [folderPath, setRunSlopesCalc]);

  // Reordering using react-beautiful-dnd
  const onDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedRules = Array.from(grRules);
    const [movedItem] = reorderedRules.splice(result.source.index, 1);
    reorderedRules.splice(result.destination.index, 0, movedItem);

    setGrRules(reorderedRules);
  };

  return (
    <div>
      <div>
        <label>Folder Path</label>
        <input
          type='text'
          placeholder='Folder Name'
          value={folderPath}
          onChange={(e) => setFolderPath(e.target.value)}
        />
      </div>

      <div>
        <label>Database Name</label>
        <input
          type="text"
          value={dbName}
          onChange={(e) => setDbName(e.target.value)}
        />
      </div>

      <div>
        <label>Collection Name</label>
        <input
          type="text"
          value={collectionName}
          onChange={(e) => setCollectionName(e.target.value)}
        />
      </div>

      <div>
        <label>Max EC2 Instances</label>
        <input
          type="number"
          value={maxServers}
          onChange={(e) => setMaxServers(e.target.value)}
          min="1"
          step="1"
        />
      </div>

      {/* Accordion for Parameters */}
      <div className="accordion accordion-flush" id="accordionFlushExample" style={{backgroundColor: 'transparent', color: 'white'}}>
        <div className="accordion-item" style={{backgroundColor: 'transparent', border: 'none'}}>
          <h2 className="accordion-header" id="flush-headingOthers">
            <button
              className="accordion-button collapsed bg-secondary text-light"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#flush-collapseOthers"
              aria-expanded="false"
              aria-controls="flush-collapseOthers"
            >
              Embankments Requirments
            </button>
          </h2>
          <div
            id="flush-collapseOthers"
            className="accordion-collapse collapse"
            aria-labelledby="flush-headingOthers"
            data-bs-parent="#accordionFlushExample"
          >
            <div className="accordion-body">
              <label>Units</label>
              <select
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                className='bg-light text-dark'
              >
                <option value="feet">Feet</option>
                <option value="meter">Meters</option>
              </select>

              <label>Max Distance to Calculate Slope</label>
              <input
                type="number"
                value={maxXToCalc}
                onChange={(e) => setMaxXToCalc(e.target.value)}
                step="5"
                className="form-control bg-light text-dark"
              />

              {/* Guardrail Warrent Rules with Drag-and-Drop */}
              <label>Guardrail Warrent Rules</label>
              <select
                className="form-control mb-3 bg-light text-dark"
                value={selectedRuleSet}
                onChange={(e) => setSelectedRuleSet(e.target.value)}
              >
                <option value="Kentucky">Kentucky</option>
                <option value="Arkansas">Arkansas</option>
                <option value="Manual">Manually Define Rules</option>
              </select>

              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="grRules">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {grRules.map((rule, index) => (
                        <Draggable key={index} draggableId={`rule-${index}`} index={index}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className="mb-3"
                            >
                              <div className="row">
                                <div className="col-3">
                                  <input
                                    type="number"
                                    value={rule.min}
                                    onChange={(e) => handleGrRuleChange(index, 'min', e.target.value)}
                                    placeholder="Min Height"
                                    className="form-control bg-light text-dark"
                                  />
                                </div>
                                <div className="col-3">
                                  <input
                                    type="number"
                                    value={rule.max}
                                    onChange={(e) => handleGrRuleChange(index, 'max', e.target.value)}
                                    placeholder="Infinity"
                                    className="form-control bg-light text-dark"
                                  />
                                </div>
                                <div className="col-3">
                                  <input
                                    type="number"
                                    value={rule.slope}
                                    onChange={(e) => handleGrRuleChange(index, 'slope', e.target.value)}
                                    placeholder="Slope Threshold"
                                    className="form-control bg-light text-dark"
                                  />
                                </div>
                                <div className="col-3 d-flex align-items-center">
                                  <button
                                    className="btn btn-link text-danger p-0 ms-4"
                                    style={{ background: 'transparent !important', color: 'white' }}
                                    onClick={() => removeGrRule(index)}
                                    title="Remove Rule"
                                  >
                                    <i className="fas fa-times" style={{ fontSize: '24px' }}></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              <button className="btn custom-dark-button" onClick={addGrRule}>
                Add Rule
              </button>

            </div>
          </div>
        </div>

        {/* Algorithm Parameters Accordion */}
        <div className="accordion-item" style={{backgroundColor: 'transparent', color: 'white'}}>
          <h2 className="accordion-header" id="flush-headingMeters">
            <button
              className="accordion-button collapsed bg-secondary text-light"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#flush-collapseMeters"
              aria-expanded="false"
              aria-controls="flush-collapseMeters"
         
            >
              Algorithm Parameters
            </button>
          </h2>
          <div
            id="flush-collapseMeters"
            className="accordion-collapse collapse"
            aria-labelledby="flush-headingMeters"
            data-bs-parent="#accordionFlushExample"
          >
            <div className="accordion-body">
              <label>Section Height (meters)</label>
              <input
                type="number"
                value={sectionHeight}
                onChange={(e) => setSectionHeight(e.target.value)}
                step="0.5"
                className="form-control bg-light text-dark"
              />

              <label>Minimum Distance Between Slope Points (meters)</label>
              <input
                type="number"
                value={minDistanceBetweenPoints}
                onChange={(e) => setMinDistanceBetweenPoints(e.target.value)}
                step="0.5"
                className="form-control bg-light text-dark"
              /> 

              <label>Bins Size (meters)</label>
              <input
                type="number"
                value={sectionBinsSize}
                onChange={(e) => setSectionBinsSize(e.target.value)}
                step="0.1"
                className="form-control bg-light text-dark"
              />

              <label>Minimum Points in Bin (%)</label>
              <input
                type="number"
                value={minBinPointsPercent * 100}
                onChange={(e) => setMinBinPointsPercent(e.target.value / 100)}
                step="1"
                className="form-control bg-light text-dark"
              />

              <label>
                <input
                  type="checkbox"
                  checked={addSlopePointsToSectionImage}
                  onChange={() => setAddSlopePointsToSectionImage(!addSlopePointsToSectionImage)}
                />{" "}
                Add Slope Points to Section Image
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={aerialData}
                  onChange={() => setAerialData(!aerialData)}
                  title='If checked, Bins size will be changed to 1.5 meters'
                />{" "}
                Aerial Data
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={twoWayAnalysis}
                  onChange={() => setTwoWayAnalysis(!twoWayAnalysis)}
                  title='If checked, New MongoDB collection will be created and the analysis will run on both POS and NEG lanes.'
                />{" "}
                Two Way Analysis
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={dlModel}
                  onChange={() => setDlModel(!dlModel)}
                  title='Check this only if the input tiles are with DL road classification'
                />{" "}
                DL Model
              </label>
            </div>
          </div>
        </div>
      </div>

      <div>
        <br />
        <button onClick={handleRunSlopesCalc}>
          Run Slopes Calc
        </button>
      </div>

      {showCheckMark && (
        <div className='check-mark'>Request {currentFolderId} Sent ✅</div>
      )}

      <div className='request-list'>
        <table>
          <thead>
            <tr>
              <th>Folder ID</th>
              <th>Start Time</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <React.Fragment key={request.id}>
                <tr onClick={() => handleToggleDetails(request.id)}>
                  <td>{request.folderId}</td>
                  <td>{request.startTime}</td>
                  <td>{request.loading ? 'Running...' : request.status}</td>
                  <td>
                    <button onClick={() => handleDeleteRequest(request.id)}>
                      <FaTrash />
                    </button>
                  </td>
                </tr>
                {request.showDetails && (
                  <tr>
                    <td colSpan="4">
                      <div className="request-details">
                        <strong>Database Name:</strong> {request.dbName} <br />
                        <strong>Collection Name:</strong> {request.collectionName} <br />
                        <strong>Max EC2 Instances:</strong> {request.maxServers} <br />
                        <strong>Slopes Params:</strong>
                        <pre>{JSON.stringify(request.slopes_params, null, 2)}</pre>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        <button className='clear-all-button' onClick={handleClearAll}>
          Remove All
        </button>
      </div>
    </div>
  );
};

export default SlopesCalcTab;
